import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ILogger,
  MediaItem,
  MediaPickerProps,
  MediaType,
  PickerMode,
  getAcceptedFormatsForMediaTypes,
  getFileDataFromUrl,
  getMediaTypeByExt,
  useGrippContext,
} from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import React, { useEffect, useRef } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Card } from 'react-native-paper';
import { useMediaUpload } from '../../hooks/useMediaUpload';

type MediaPickerExtendedProps = MediaPickerProps & { 
  autoUpload?: boolean; 
  onFileSelected?: (file: File) => void;
  name?: string;
  categories?: Array<{ id: string }>;
  autoTrigger?: boolean;
};

export const MediaPicker = (props: MediaPickerExtendedProps) => {
  const {
    acceptedMediaTypes = [MediaType.Image],
    autoUpload = true,
    onFileSelected,
    workspaceId,
    name,
    categories,
    autoTrigger = false,
  } = props;
  const { logger } = useGrippContext();
  let icon;
  if (props.icon) {
    icon = props.icon;
  } else {
    icon = props.mode === PickerMode.CAMERA ? 'camera' : 'image-plus';
  }
  const fileUploadTriggerTimeout = 100;
  const iconSize = props.iconSize || 24;
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const uploadMedia = useMediaUpload({
    onProgress: (mediaId: string, progress: number) => {
      props.setProgress(mediaId, progress);
    },
    onComplete: (mediaId: string) => {
      props.setUploading && props.setUploading(false);
    },
    onStart: (mediaItem: MediaItem) => {
      props.setUploading && props.setUploading(true);
      props.setMedia({
        ...mediaItem,
        isUploading: true,
        uploadProgress: 0,
      });
    },
  });
  const captureProps: any = {};
  if (props.mode === PickerMode.CAMERA) {
    captureProps.capture = 'environment';
  }

  const acceptedFormats = getAcceptedFormatsForMediaTypes(acceptedMediaTypes);

  // Auto-trigger file picker on mount if autoTrigger is true
  useEffect(() => {
    if (autoTrigger && fileUploadRef.current) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        fileUploadRef.current?.click();
      }, fileUploadTriggerTimeout);
      return () => clearTimeout(timer);
    }
  }, [autoTrigger]);

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    const file = e.target.files[0];
    if (!shouldProcessFile(file.name, acceptedMediaTypes, logger)) {
      return;
    }
    if (autoUpload) {
      await uploadMedia(file, workspaceId, name, categories);
    } else if (onFileSelected) {
      onFileSelected(file);
    }
  };

  return (
    <>
      <input
        ref={fileUploadRef}
        type="file"
        style={{ display: 'none' }}
        accept={acceptedFormats}
        onChange={handleFileInputChange}
        multiple={false}
        {...captureProps}
      />
      {props.isIconInBox ? (
        <MaterialCommunityIcons
          name={props.icon as any || 'camera-outline'}
          color={props.iconColor || Colors.white}
          size={props.iconSize || 16}
          onPress={() => fileUploadRef.current && fileUploadRef.current.click()}
        />
      ) : (
        <Pressable onPress={() => fileUploadRef.current && fileUploadRef.current.click()}>
          {props?.customTrigger ? (
            props.customTrigger
          ) : (
            <Card
              mode={props.cardStyle || undefined}
              style={props.style || styles.cardIcon}
            >
              <MaterialCommunityIcons
                name={icon as any}
                color={props.iconColor || Colors.black}
                size={iconSize}
              />
            </Card>
          )}
        </Pressable>
      )}
    </>
  );
};

const shouldProcessFile = (
  fileName: string,
  acceptedMediaTypes: MediaType[],
  logger?: ILogger
) => {
  try {
    const ext = getFileDataFromUrl(fileName).ext;
    const fileMediaType = getMediaTypeByExt(ext);

    if (!acceptedMediaTypes.includes(fileMediaType)) {
      logger?.warning(
        `Media picker web - format does not match accepted types: ${fileMediaType}`
      );
      return false;
    }

    return true;
  } catch (e) {
    logger?.warning(`Media picker web - failed to check file format: ${e}`);
    return false;
  }
};

const styles = StyleSheet.create({
  imageCard: {
    height: 100,
    width: 100,
  },
  imageCardContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 20,
  },
  cardIcon: {
    height: 45,
    width: 45,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginTop: -30,
    marginRight: -20,
  },
  progress: {
    marginTop: 5,
  },
});
